// frontend/spa.mjs - Simplified SPA

/**
 * Single Page Application controller for the track player
 */
export default class TrackPlayerSPA {
    constructor() {
        this.currentPlayer = null;
        this.trackSelect = null;
        this.playerContainer = null;

        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupDOM();
            });
        } else {
            this.setupDOM();
        }
    }
    
    setupDOM() {
        this.trackSelect = document.getElementById('track-select');
        this.playerContainer = document.getElementById('player-container');

        if (!this.trackSelect || !this.playerContainer) {
            console.warn('SPA: Required DOM elements not found');
            return;
        }

        // No audio priming needed

        this.setupEventListeners();
        this.loadInitialTrack();

        console.log('SPA initialized');
    }
    
    
    setupEventListeners() {
        // Track selection handler
        this.trackSelect.addEventListener('change', (e) => {
            this.handleTrackChange(e.target.value);
        });
        
        // Browser back/forward handler
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.track) {
                this.trackSelect.value = e.state.track;
                this.handleTrackChange(e.state.track);
            } else {
                this.trackSelect.value = '';
                this.clearPlayer();
            }
        });
    }
    
    loadInitialTrack() {
        // Check URL for initial track (for direct links)
        const urlParams = new URLSearchParams(window.location.search);
        const initialTrack = urlParams.get('track');

        if (initialTrack) {
            this.trackSelect.value = initialTrack;
            this.handleTrackChange(initialTrack);
        }
    }
    
    async handleTrackChange(trackName) {
        console.log('Track selected:', trackName);

        if (!trackName) {
            this.clearPlayer();
            this.updateURL('');
            return;
        }

        // Show loading
        this.showLoading();

        try {
            // Remove old player if exists
            if (this.currentPlayer) {
                console.log('Cleaning up previous player...');

                // Call cleanup method if it exists
                if (typeof this.currentPlayer.cleanup === 'function') {
                    this.currentPlayer.cleanup();
                }

                // Remove from DOM
                this.currentPlayer.remove();
                this.currentPlayer = null;

                console.log('Previous player cleaned up');
            }

            // Create new player
            await this.loadTrack(trackName);

            // Update URL
            this.updateURL(trackName);

        } catch (error) {
            console.error('Failed to load track:', error);
            this.showError(`Failed to load track: ${trackName}`);
        }
    }
    
    showLoading() {
        this.playerContainer.innerHTML = `
            <div class="loading">
                <div class="spinner"></div>
                Loading track...
            </div>
        `;
        this.playerContainer.classList.remove('has-player');
    }
    
    async loadTrack(trackName) {
        // Verify track exists
        const metaUrl = `./tracks/${encodeURIComponent(trackName)}/meta.json`;
        
        try {
            const response = await fetch(metaUrl);
            if (!response.ok) {
                throw new Error(`Track not found: ${response.status}`);
            }
            
            // Create player element
            const player = document.createElement('lyp-player');
            player.setAttribute('src', metaUrl);
            
            // Clear container and add player
            this.playerContainer.innerHTML = '';
            this.playerContainer.appendChild(player);
            this.playerContainer.classList.add('has-player');
            
            this.currentPlayer = player;
            
            console.log(`Loaded track: ${trackName}`);
            
        } catch (error) {
            throw new Error(`Could not load track metadata: ${error.message}`);
        }
    }
    
    showError(message) {
        this.playerContainer.innerHTML = `
            <div style="color: #ff4444; text-align: center;">
                ❌ ${message}
            </div>
        `;
        this.playerContainer.classList.remove('has-player');
    }
    
    clearPlayer() {
        this.playerContainer.innerHTML = 'Select a track above to start practicing';
        this.playerContainer.classList.remove('has-player');
        
        // IMPORTANT: Cleanup current player before clearing
        if (this.currentPlayer) {
            console.log('Cleaning up current player...');
            
            // Call cleanup method if it exists
            if (typeof this.currentPlayer.cleanup === 'function') {
                this.currentPlayer.cleanup();
            }
            
            this.currentPlayer.remove();
            this.currentPlayer = null;
            
            console.log('Player cleared and cleaned up');
        }
    }
    
    updateURL(trackName) {
        if (trackName) {
            const newUrl = `${window.location.pathname}?track=${encodeURIComponent(trackName)}`;
            window.history.pushState({ track: trackName }, '', newUrl);
        } else {
            window.history.pushState({}, '', window.location.pathname);
        }
    }
    
    // Public API
    getCurrentTrack() {
        return this.trackSelect?.value || null;
    }
    
    setTrack(trackName) {
        if (this.trackSelect) {
            this.trackSelect.value = trackName;
            this.handleTrackChange(trackName);
        }
    }
}
